import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import useSiteConfig from "@/lib/useSiteConfig";
import { toast } from "sonner";
import { Camera, Film, Users, MessageSquare, Play } from "lucide-react";
import PhoneInput from "@/components/common/PhoneInput";
import FindYourStayCTA from "@/components/common/FindYourStayCTA";
import SafeHtml from "@/components/common/SafeHtml";
import { track } from "@/components/common/Analytics";

/**
 * For Creators and Commercial Shoots — dedicated lead form + a portfolio gallery of
 * our spaces actually being used for shoots. Admin manages the gallery from
 * Site config → Shoots.
 */
const PROJECT_TYPES = [
  "Fashion / editorial shoot",
  "Brand / product shoot",
  "Film / TV / OTT",
  "Music video",
  "Content creator (personal)",
  "Wedding / pre-wedding",
  "Other (tell us below)",
];

const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
const spellOut = (n) => (n >= 0 && n < NUMBER_WORDS.length ? NUMBER_WORDS[n][0].toUpperCase() + NUMBER_WORDS[n].slice(1) : `${n}`);

const isVideoUrl = (url) => /\.(mp4|mov|webm|m4v|ogg)(\?|$)/i.test(url || "") || /youtube\.com|youtu\.be|vimeo\.com/i.test(url || "");
const isYouTubeOrVimeo = (url) => /youtube\.com|youtu\.be|vimeo\.com/i.test(url || "");
const isInstagram = (url) => /instagram\.com\/(p|reel|tv)\//i.test(url || "");
const toInstagramEmbed = (url) => {
  // Instagram's official embed URL format. Accepts both www.instagram.com/p/XYZ
  // and www.instagram.com/reel/XYZ; trailing params are stripped by the
  // embed endpoint itself.
  const m = String(url).match(/instagram\.com\/(p|reel|tv)\/([\w-]+)/i);
  if (!m) return url;
  return `https://www.instagram.com/${m[1]}/${m[2]}/embed/`;
};
const toEmbed = (url) => {
  if (/youtu\.be\//.test(url)) return url.replace(/youtu\.be\/([\w-]+).*/, "https://www.youtube.com/embed/$1");
  if (/youtube\.com\/watch/.test(url)) return url.replace(/watch\?v=([\w-]+).*/, "embed/$1");
  if (/vimeo\.com\/(\d+)/.test(url)) return url.replace(/vimeo\.com\/(\d+).*/, "https://player.vimeo.com/video/$1");
  return url;
};

export default function Shoots() {
  const cfg = useSiteConfig();
  const propCount = Math.max(1, Number(cfg.active_properties_count || 0));
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    project_type: PROJECT_TYPES[0], crew_size: 4, shoot_date: "", duration: "Half day",
    preferred_property: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const media = cfg.shoot_media || [];

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = [
        `Project: ${form.project_type}`,
        `Crew size: ${form.crew_size}`,
        `Shoot date: ${form.shoot_date || "flexible"}`,
        `Duration: ${form.duration}`,
        `Preferred property: ${form.preferred_property || "open"}`,
        "",
        form.message,
      ].join("\n");
      await api.post("/contact", {
        name: form.name, email: form.email, phone: form.phone,
        message: body, kind: "shoot_inquiry",
      });
      track("generate_lead", { source: "shoots_form", content_category: "shoot_inquiry" });
      toast.success("Thanks! Our team will reach out within 24 hours.");
      setForm({ ...form, name: "", email: "", phone: "", shoot_date: "", message: "" });
    } catch { toast.error("Couldn't send. Please WhatsApp us instead."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="shoots-page">
      <Helmet>
        <title>For Creators & Commercial Shoots · Vyagram Experiences</title>
        <meta name="description" content="Book a Vyagram stay for your commercial shoot, brand campaign, film, or creator content. Dedicated inquiry form, flexible rates, pre-approved locations." />
      </Helmet>

      <section className="relative isolate overflow-hidden bg-forest">
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:px-10">
          <p className="font-accent text-sm text-gold uppercase tracking-[0.25em]">For creators &amp; commercial shoots</p>
          <h1 className="mt-3 max-w-4xl font-display text-4xl text-parchment sm:text-6xl lg:text-7xl">
            {spellOut(propCount)} homes, <span className="font-script text-gold">one production address.</span>
          </h1>
          <p className="mt-5 max-w-2xl font-editorial text-lg text-parchment/85 sm:text-xl">
            {spellOut(propCount)} design-led stays across South Delhi and Gurugram &mdash; Japandi, cinematic noir,
            illustrated maximalist, cyberpunk and more. Each built floor-up by architects.
            Tell us about your project and we&rsquo;ll come back with a tailored quote.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: <Camera size={20} className="text-gold"/>, title: "Pre-approved", body: "Shoot-friendly stays with flexible call-times and natural-light maps." },
            { icon: <Film size={20} className="text-gold"/>, title: "Film-grade rooms", body: "Minimal prop clutter, strong aesthetic themes, production-ready." },
            { icon: <Users size={20} className="text-gold"/>, title: "On-ground crew", body: "Our team can coordinate with yours on the day of shoot." },
          ].map((c) => (
            <div key={c.title} className="rounded-2xl border border-forest/10 bg-white p-5 shadow-card">
              <div className="flex items-center gap-2">{c.icon}<p className="font-display text-lg">{c.title}</p></div>
              <p className="editorial-body mt-2 !text-sm">{c.body}</p>
            </div>
          ))}
        </div>

        {cfg.shoot_intro_html && (
          <SafeHtml html={cfg.shoot_intro_html} className="mt-10 editorial-body prose max-w-none text-noir/85" testId="shoots-intro" />
        )}

        <form onSubmit={onSubmit} className="mt-10 rounded-2xl border border-forest/10 bg-white p-6 shadow-card sm:p-8" data-testid="shoots-form">
          <p className="vyg-eyebrow">Project brief</p>
          <h2 className="mt-2 font-display text-3xl">Tell us what you&rsquo;re shooting</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Field label="Your name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="shoots-name" className="inp" /></Field>
            <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="shoots-email" className="inp" /></Field>
            <Field label="Phone / WhatsApp" className="sm:col-span-2">
              <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="shoots-phone" />
            </Field>
            <Field label="Project type">
              <select value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} data-testid="shoots-type" className="inp">
                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Crew size"><input type="number" min={1} max={60} value={form.crew_size} onChange={(e) => setForm({ ...form, crew_size: Number(e.target.value) })} data-testid="shoots-crew" className="inp" /></Field>
            <Field label="Preferred shoot date">
              <input type="date" value={form.shoot_date} onChange={(e) => setForm({ ...form, shoot_date: e.target.value })} data-testid="shoots-date" className="inp" />
            </Field>
            <Field label="Duration">
              <select value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="shoots-duration" className="inp">
                {["Half day (4 hrs)", "Full day (10 hrs)", "2 days", "3+ days"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Preferred property (optional)" className="sm:col-span-2">
              <input placeholder="e.g. Noir, Sombre, Cyberpunk — or ‘open to suggestions’" value={form.preferred_property} onChange={(e) => setForm({ ...form, preferred_property: e.target.value })} data-testid="shoots-preferred" className="inp" />
            </Field>
            <Field label="Tell us more about your shoot" className="sm:col-span-2">
              <textarea rows={4} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="What's the mood? Any references? Special gear or equipment list we should know about? Brands / publications involved?"
                data-testid="shoots-message" className="inp" />
            </Field>
          </div>
          <button type="submit" disabled={submitting} className="vyg-cta mt-7 w-full sm:w-auto" data-testid="shoots-submit">
            {submitting ? "Sending…" : (<><MessageSquare size={14}/> Send inquiry</>)}
          </button>
        </form>
      </section>

      {/* Shoot media gallery — admin-editable portfolio below the form.
          Always rendered so the section anchor is stable; shows a placeholder
          when no media has been added yet. */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-10" data-testid="shoots-gallery">
        <p className="vyg-eyebrow">Portfolio</p>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl">Our spaces, <span className="font-script text-gold">on set</span></h2>
        <p className="editorial-body mt-3 max-w-2xl !text-base text-noir/70">
          A glimpse of past shoots across the portfolio — brand campaigns, editorial,
          music videos and creator stories. Direct video uploads, YouTube/Vimeo and
          Instagram embeds are all supported.
        </p>
        {media.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-forest/15 bg-mist/30 px-6 py-12 text-center text-sm text-noir/60" data-testid="shoots-gallery-empty">
            Portfolio pieces will appear here. Admins can add photos, videos, and Instagram embeds from <span className="font-medium text-forest">Site config → Shoot media</span>.
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4" data-testid="shoots-gallery-grid">
            {media.map((m, i) => {
              // On the desktop grid we add subtle col/row spans for visual
              // rhythm. On mobile we keep every tile a clean 1×1 so nothing
              // overflows or sits awkwardly half-cropped.
              const isWide = i % 5 === 2;
              const isTall = i % 7 === 3;
              const span = `${isWide ? "lg:col-span-2 " : ""}${isTall ? "lg:row-span-2 " : ""}`.trim();
              return <GalleryTile key={typeof m === "string" ? m : (m.url || `m-${i}`)} m={m} span={span} idx={i} />;
            })}
          </div>
        )}
      </section>

      <FindYourStayCTA
        eyebrow="Just want to browse?"
        title="See all our shoot-friendly stays"
        subtitle="Quiet design, great light, room to work. Browse the full portfolio."
        primaryLabel="Browse all stays"
        secondaryLabel="Or chat on WhatsApp"
        secondaryTo="/contact"
      />

      <style>{`.inp{width:100%;border:1px solid rgb(31 71 58 / .2);border-radius:0.65rem;padding:0.65rem 0.9rem;background:#fff;font-size:0.9rem;outline:none;color:#1C1C1C}.inp:focus{border-color:#1F473A}`}</style>
    </div>
  );
}

function GalleryTile({ m, span, idx }) {
  const url = typeof m === "string" ? m : m.url;
  const caption = typeof m === "object" ? m.caption : "";
  const declaredType = typeof m === "object" && m.type ? m.type : "";
  const type = declaredType || (isInstagram(url) ? "instagram" : isVideoUrl(url) ? "video" : "image");

  // Stable per-tile aspect on mobile so the grid never collapses to a tiny
  // 140px row when a video/IG embed needs more height. On desktop the
  // optional col/row spans (`span`) layer in for visual rhythm.
  const baseAspect = type === "instagram" ? "aspect-[3/4]" : "aspect-[4/3]";
  const baseClasses = `group relative overflow-hidden rounded-2xl bg-white shadow-card ${baseAspect} lg:aspect-auto lg:min-h-[220px] ${span}`;

  // Instagram reels / posts — uses Instagram's official embed iframe. Has
  // native click-through to Instagram, handles autoplay correctly and
  // displays the caption from the post.
  if (type === "instagram" || isInstagram(url)) {
    return (
      <figure data-testid={`shoot-tile-${idx}`} className={baseClasses}>
        <iframe
          src={toInstagramEmbed(url)}
          title={caption || `Instagram shoot ${idx + 1}`}
          className="h-full w-full"
          loading="lazy"
          scrolling="no"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
        />
        {caption && <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-noir/85 to-transparent px-3 py-2 text-[11px] text-white">{caption}</figcaption>}
      </figure>
    );
  }

  if (type === "video" && isYouTubeOrVimeo(url)) {
    return (
      <figure data-testid={`shoot-tile-${idx}`} className={`group relative overflow-hidden rounded-2xl bg-noir/10 shadow-card ${baseAspect} lg:aspect-auto lg:min-h-[220px] ${span}`}>
        <iframe
          src={toEmbed(url)}
          title={caption || `Shoot media ${idx + 1}`}
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
        {caption && <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-noir/85 to-transparent px-3 py-2 text-[11px] text-white">{caption}</figcaption>}
      </figure>
    );
  }
  if (type === "video") {
    return (
      <figure data-testid={`shoot-tile-${idx}`} className={`group relative overflow-hidden rounded-2xl bg-noir shadow-card ${baseAspect} lg:aspect-auto lg:min-h-[220px] ${span}`}>
        <video src={url} className="h-full w-full object-cover" controls muted loop playsInline preload="metadata" />
        <span className="pointer-events-none absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[10px] text-white backdrop-blur"><Play size={10}/> Video</span>
        {caption && <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-noir/85 to-transparent px-3 py-2 text-[11px] text-white">{caption}</figcaption>}
      </figure>
    );
  }
  return (
    <figure data-testid={`shoot-tile-${idx}`} className={`group relative overflow-hidden rounded-2xl shadow-card ${baseAspect} lg:aspect-auto lg:min-h-[220px] ${span}`}>
      <img src={url} alt={caption || `Shoot media ${idx + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      {caption && (
        <figcaption className="pointer-events-none absolute bottom-0 inset-x-0 bg-gradient-to-t from-noir/85 to-transparent px-3 py-2 text-[11px] text-white sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] uppercase tracking-wider text-amber">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
